import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  Divider
} from '@mui/material';
import { 
  ArrowBack, 
  CloudUpload,
  Add,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const steps = ['기본 정보', '상세 정보', '모집 조건', '검토 및 등록'];

const categories = [
  '지역별', '리포트', '스토어리뷰', '뷰티', '음식', '전자제품', '패션', '라이프스타일'
];

const locations = [
  '서울', '경기', '인천', '대전', '대구', '부산', '울산', '광주', '세종',
  '강원', '충북', '충남', '경북', '경남', '전북', '전남', '제주', '전국'
];

const CampaignCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    maxApplicants: '',
    reward: '',
    deadline: '',
    requirements: '',
    images: [] as string[]
  });

  const handleBack = () => {
    navigate('/mypage/advertiser');
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBackStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = () => {
    // TODO: Implement campaign creation
    console.log('Campaign data:', formData);
    navigate('/mypage/advertiser');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="캠페인 제목"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
                helperText="캠페인을 대표하는 제목을 입력해주세요"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>카테고리</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="카테고리"
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>활동 지역</InputLabel>
                <Select
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  label="활동 지역"
                >
                  {locations.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="캠페인 설명"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={4}
                required
                helperText="캠페인에 대한 상세한 설명을 입력해주세요"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                캠페인 이미지
              </Typography>
              <Box sx={{ border: '2px dashed', borderColor: 'grey.300', borderRadius: 2, p: 3, textAlign: 'center' }}>
                <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  이미지를 업로드하거나 클릭하여 선택하세요
                </Typography>
                <Button variant="outlined" startIcon={<Add />}>
                  이미지 추가
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="참여 요구사항"
                value={formData.requirements}
                onChange={(e) => handleInputChange('requirements', e.target.value)}
                multiline
                rows={3}
                helperText="참여자에게 요구하는 조건이나 준비사항을 입력해주세요"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="모집 인원"
                type="number"
                value={formData.maxApplicants}
                onChange={(e) => handleInputChange('maxApplicants', e.target.value)}
                required
                helperText="최대 모집 인원을 입력해주세요"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="보상 (포인트)"
                type="number"
                value={formData.reward}
                onChange={(e) => handleInputChange('reward', e.target.value)}
                required
                helperText="참여자에게 지급할 포인트를 입력해주세요"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="신청 마감일"
                type="date"
                value={formData.deadline}
                onChange={(e) => handleInputChange('deadline', e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
                helperText="캠페인 신청 마감일을 선택해주세요"
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              캠페인 정보 확인
            </Typography>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">캠페인 제목</Typography>
                    <Typography variant="body1">{formData.title || '입력되지 않음'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">카테고리</Typography>
                    <Typography variant="body1">{formData.category || '입력되지 않음'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">활동 지역</Typography>
                    <Typography variant="body1">{formData.location || '입력되지 않음'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">캠페인 설명</Typography>
                    <Typography variant="body1">{formData.description || '입력되지 않음'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">모집 인원</Typography>
                    <Typography variant="body1">{formData.maxApplicants || '입력되지 않음'}명</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">보상</Typography>
                    <Typography variant="body1">{formData.reward || '입력되지 않음'}P</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">마감일</Typography>
                    <Typography variant="body1">{formData.deadline || '입력되지 않음'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Typography variant="body2" color="text.secondary">
              위 정보로 캠페인이 등록됩니다. 등록 후에는 수정이 제한될 수 있습니다.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ pb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          뒤로
        </Button>
        <Typography variant="h5" component="h1">
          캠페인 등록
        </Typography>
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card>
        <CardContent>
          {renderStepContent(activeStep)}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBackStep}
        >
          이전
        </Button>
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
            >
              캠페인 등록
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
            >
              다음
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default CampaignCreatePage; 